import { CliError } from "@/lib/cli/cli-error";
import { CLI_ERROR_CODES } from "@/lib/shared/errors-constants";

interface FetchResponseOptions {
  url: URL;
  method: string;
  headers: HeadersInit;
  body: unknown;
  deadline: number;
  baseUrl: string;
}

export async function fetchResponse({
  url,
  method,
  headers,
  body,
  deadline,
  baseUrl,
}: FetchResponseOptions): Promise<[Response, string]> {
  const controller = new AbortController();
  let abortedBy: "timeout" | "interrupt" | null = null;
  const timer = setTimeout(() => {
    abortedBy ??= "timeout";
    controller.abort();
  }, deadline);
  const onInterrupt = () => {
    abortedBy ??= "interrupt";
    controller.abort();
  };
  process.once("SIGINT", onInterrupt);

  try {
    const response = await fetch(url, {
      method,
      headers,
      signal: controller.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    return [response, await response.text()];
  } catch (cause) {
    if (abortedBy === "interrupt") {
      throw new CliError(`${method} ${url.pathname} interrupted.`, 130, {
        code: CLI_ERROR_CODES.INTERRUPTED,
      });
    }
    if (abortedBy === "timeout") {
      throw new CliError(
        `${method} ${url.pathname} timed out after ${deadline}ms. Raise HEPHA_TIMEOUT_MS if the platform is simply slow.`,
        1,
        { code: CLI_ERROR_CODES.TIMEOUT },
      );
    }
    throw new CliError(
      `Cannot reach ${baseUrl}. Set HEPHA_URL if the platform lives elsewhere. (${String(cause)})`,
      1,
      { code: CLI_ERROR_CODES.NETWORK },
    );
  } finally {
    clearTimeout(timer);
    process.removeListener("SIGINT", onInterrupt);
  }
}
